import { useEffect, useState } from 'react';
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../api/api';
import NetworkStorageExplorer from '../exo/explorer/NetworkStorageExplorer';

const ONLINE_DRIVERS = ['dropbox', 'onedrive'];

/**
 * Second step of the network storage wizard. Lets the user pick a namespace
 * (online providers) and browse the now-connected storage to select the root
 * and project folders. The layout renders immediately; only the file explorer
 * shows its own loading state while it fetches folders.
 */
export const StepStorageOptions = ({
  dataCollection,
  updateDataCollection,
  updateValidation,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const storageId = dataCollection.id;

  const [storage, setStorage] = useState(null);
  const [namespaces, setNamespaces] = useState([]);
  const [namespaceValue, setNamespaceValue] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!config || !storageId) return;
    updateValidation(false);

    DBRequest({
      config,
      path: `network-storages/${storageId}`,
      method: API_GET,
      onResponse: (res) => {
        const ns = res.data;
        setStorage(ns);

        const opts = ns.options || {};
        setNamespaceValue(opts.teamFolderId || opts.driveId || '');

        updateDataCollection((prev) => ({
          ...prev,
          driver: ns.driver,
          rootDirectory: prev.rootDirectory ?? ns.rootDirectory ?? '',
          projectRootDirectory:
            prev.projectRootDirectory ?? ns.projectRootDirectory ?? '',
        }));
        updateValidation(true);

        if (ONLINE_DRIVERS.includes(ns.driver)) loadNamespaces();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, storageId]);

  function loadNamespaces() {
    DBRequest({
      config,
      path: `network-storages/${storageId}/namespaces`,
      method: API_GET,
      onResponse: (res) => setNamespaces(res.data.namespaces || []),
    });
  }

  function handleNamespaceChange(value) {
    setNamespaceValue(value);

    const payload =
      storage.driver === 'dropbox'
        ? { teamFolderId: value }
        : { driveId: value, directoryType: 'drives' };

    updateDataCollection((prev) => ({ ...prev, ...payload }));

    // Persist the namespace immediately so the explorer browses inside it.
    DBRequest({
      config,
      path: `network-storages/${storageId}`,
      method: API_PATCH({ ...payload }),
      onResponse: () => setReloadKey((key) => key + 1),
    });
  }

  const isOnline = storage ? ONLINE_DRIVERS.includes(storage.driver) : false;

  return (
    <Box className="flex flex-col gap-4 w-full pt-2">
      {isOnline && namespaces.length > 0 && (
        <TextField
          select
          fullWidth
          label={t('Namespace')}
          value={namespaceValue}
          onChange={(e) => handleNamespaceChange(e.target.value)}
        >
          {namespaces.map((namespace) => (
            <MenuItem key={namespace.id} value={namespace.id}>
              {namespace.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Box className="flex flex-col gap-1">
        <Typography variant="h6">{t('Root Directory')}</Typography>
        <NetworkStorageExplorer
          key={`root-${storageId}-${reloadKey}`}
          storageId={storageId}
          value={dataCollection.rootDirectory || ''}
          onChange={(path) =>
            updateDataCollection((prev) => ({ ...prev, rootDirectory: path }))
          }
        />
      </Box>

      <TextField
        fullWidth
        label={t('Project Directory')}
        value={dataCollection.projectRootDirectory || ''}
        onChange={(e) =>
          updateDataCollection((prev) => ({
            ...prev,
            projectRootDirectory: e.target.value,
          }))
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" className="monospace whitespace-nowrap opacity-70">
                /{dataCollection.rootDirectory ? `${dataCollection.rootDirectory}/` : ''}
              </Typography>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};
